import { createElement, lazy, useState } from 'react';

// Code splitting (SEO phase 0, section 6 item 1). Each app screen is its own file,
// downloaded the first time it is opened, so a visitor to the homepage or a guide
// no longer downloads the editor, checkout and admin panel.
//
// lazyPage(() => import('./pages/X')) returns a component that:
//   - has .preload(), which starts the download early (index.js calls it for the
//     address being opened, before the first render, so a direct visit renders the
//     real page straight away instead of a blank loading state);
//   - renders the page directly once it has been downloaded, so a preloaded page
//     never shows the loading state at all;
//   - retries a failed download once, after a short pause (a dropped mobile
//     connection). A download that still fails reaches ChunkErrorBoundary; once the
//     visitor moves on, the next visit to the screen downloads it afresh (see below).

const RETRY_DELAY_MS = 1500;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// React remembers a lazy component's failure for good: without a fresh one, coming
// back to a screen whose download failed re-shows the old error and never downloads
// it again, even once the connection is back (Codex review, PR 425). The fresh one is
// made only when ChunkErrorBoundary clears its message (the visitor moved to another
// address), never straight after the failure: React re-renders the failed screen once
// itself, and a fresh download there would retry in a loop instead of reaching the
// message when a file is really missing.
const failedScreens = new Set();

export const resetFailedScreens = () => {
  failedScreens.forEach(reset => reset());
  failedScreens.clear();
};

export const lazyPage = (loader) => {
  let loaded = null;
  let pending = null;
  let LazyPage = null;
  const reset = () => { LazyPage = lazy(load); };

  const load = () => {
    if (!pending) {
      // webpack forgets a failed file, so calling loader() again really re-downloads it.
      pending = loader()
        .catch(() => wait(RETRY_DELAY_MS).then(loader))
        .then(
          (module) => { loaded = module.default; return module; },
          (error) => { pending = null; failedScreens.add(reset); throw error; },
        );
    }
    return pending;
  };

  LazyPage = lazy(load);

  const Page = (props) => {
    // Chosen once per mounted page: switching from the lazy wrapper to the real
    // component later would remount the page and lose what the visitor typed.
    const [Component] = useState(() => loaded || LazyPage);
    return createElement(Component, props);
  };
  Page.preload = load;
  return Page;
};
